// Shao Kahn-style narrator voice clips, served as static MP3s from /voice/.
//
// Two surfaces:
//   1. App startup (one of seven generic taunts on first interaction)
//   2. Per-person page (score-band-themed taunt when a person opens)
//
// We respect a `voiceMuted` flag in localStorage so users can shut him up.
// Browsers block autoplay-with-sound until the user has interacted with the
// page; the login form button click counts, so post-login we can play freely.

const STARTUP_POOL = [
  // Original 7 — softer "welcome to the docket" vibes
  "/voice/startup_classic.mp3",
  "/voice/startup_lots.mp3",
  "/voice/startup_getting.mp3",
  "/voice/startup_docket.mp3",
  "/voice/startup_friday.mp3",
  "/voice/startup_curse.mp3",
  "/voice/startup_welcome.mp3",
  // New 3 — leans HARD into the brand line. Mixed in so the main page
  // isn't ALWAYS f-bombs but lands on one every few visits.
  "/voice/startup_who_today.mp3",        // "Welcome back. Now let's see who done fucked up today."
  "/voice/startup_another_file.mp3",     // "Another file. Another fool who done fucked up."
  "/voice/startup_find_out.mp3",         // "You came here to find out who fucked around. Now you find out."
];

const TIER_POOLS: Record<0 | 1 | 2 | 3 | 4, string[]> = {
  0: ["/voice/tier_clean_suspicious.mp3", "/voice/tier_clean_boring.mp3"],
  1: ["/voice/tier_clean_suspicious.mp3", "/voice/tier_mid_bumps.mp3"],
  2: [
    "/voice/tier_mid_mediocre.mp3",
    "/voice/tier_mid_bumps.mp3",
    // New 2 — first whiff of "you fucked up" energy without going nuclear
    "/voice/tier_mid_half_life.mp3",       // "Half a life of trying. Half a life of fucking up."
    "/voice/tier_mid_recoverable.mp3",     // "Fucked up enough to be here. Not enough to be unrecoverable. Yet."
  ],
  3: [
    "/voice/tier_chaos_scale.mp3",
    "/voice/tier_chaos_reckless.mp3",
    "/voice/tier_chaos_commitment.mp3",
    // New 3 — pattern-of-fucking-up theme
    "/voice/tier_chaos_again_again.mp3",        // "You done fucked up... again. And again. And again."
    "/voice/tier_chaos_patterns.mp3",           // "Patterns of fucking up, my friend. Patterns."
    "/voice/tier_chaos_different_outcomes.mp3", // "Can't keep fucking up like this and expecting different outcomes."
  ],
  4: [
    "/voice/tier_cata_masterclass.mp3",
    "/voice/tier_cata_epic.mp3",
    // tier_cata_career.mp3 was inadvertently overwritten when the new
    // gen_voices.py reused the slug — current file on disk is now the
    // "you done fucked up so much, it's basically a career path" line,
    // which still fits this tier well, so we keep it in the rotation.
    "/voice/tier_cata_career.mp3",
    "/voice/tier_cata_commission.mp3",
    // New 4 — full "you done fucked up" Shao Kahn payload for cataclysm
    "/voice/tier_cata_fucked_baaad.mp3",         // "You done fucked up. You done fucked up baaad."
    "/voice/tier_cata_fuck_around_find_out.mp3", // "You fucked around. Now you find out."
    "/voice/tier_cata_one_big_fuckup.mp3",       // "This whole damn file is just one big you-done-fucked-up."
    "/voice/tier_cata_gallery.mp3",              // "If fucking up was an art form, you'd have your own gallery."
  ],
};

const STORAGE_KEY = "voiceMuted";

// Remember the last clip played per pool so we don't repeat back-to-back.
const lastPlayed: Record<string, string> = {};

let currentAudio: HTMLAudioElement | null = null;

export function isMuted(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setMuted(muted: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, muted ? "1" : "0");
  } catch {}
  if (muted && currentAudio) {
    currentAudio.pause();
    currentAudio = null;
  }
}

function pickFresh(pool: string[], poolKey: string): string {
  if (pool.length === 0) return "";
  if (pool.length === 1) return pool[0]!;
  let pick: string;
  let attempts = 0;
  do {
    pick = pool[Math.floor(Math.random() * pool.length)]!;
    attempts++;
  } while (pick === lastPlayed[poolKey] && attempts < 5);
  lastPlayed[poolKey] = pick;
  return pick;
}

// Voice <-> music coordination: when a voice clip is playing we want the
// background music to drop in volume so the narration cuts through. Music
// module installs these hooks via setMusicHooks(); voice.ts calls them
// regardless of whether the music player is on (no-op if hooks unset).
let musicHooks: { duck: () => void; unduck: () => void } | null = null;
export function setMusicHooks(h: { duck: () => void; unduck: () => void } | null): void {
  musicHooks = h;
}

function play(src: string): void {
  if (!src || isMuted()) return;
  try {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio = null;
      // If we're cutting an in-flight clip short, release the duck so we
      // don't end up doubly-ducked.
      try { musicHooks?.unduck(); } catch {}
    }
    const audio = new Audio(src);
    audio.volume = 0.85;
    currentAudio = audio;
    // Duck the background music BEFORE the clip's first frame plays so
    // the voice never gets stepped on. Unduck on end OR error.
    try { musicHooks?.duck(); } catch {}
    let unducked = false;
    const releaseDuck = () => {
      if (unducked) return;
      unducked = true;
      try { musicHooks?.unduck(); } catch {}
    };
    audio.play().catch(() => {
      currentAudio = null;
      releaseDuck();
    });
    audio.onended = () => {
      if (currentAudio === audio) currentAudio = null;
      releaseDuck();
    };
    audio.onerror = () => {
      if (currentAudio === audio) currentAudio = null;
      releaseDuck();
    };
  } catch {
    // Any audio failure is non-fatal.
    try { musicHooks?.unduck(); } catch {}
  }
}

export function playStartup(): void {
  play(pickFresh(STARTUP_POOL, "startup"));
}

function bandFor(score: number): 0 | 1 | 2 | 3 | 4 {
  if (score <= 15) return 0;
  if (score <= 35) return 1;
  if (score <= 55) return 2;
  if (score <= 75) return 3;
  return 4;
}

export function playForScore(score: number | null | undefined): void {
  if (score === null || score === undefined) return;
  const band = bandFor(score);
  play(pickFresh(TIER_POOLS[band], `tier_${band}`));
}

export function stopVoice(): void {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio = null;
  }
}
